import { useMemo, useRef, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import { gsap } from 'gsap';
import { useApp } from '../context/AppContext';
import { formatNaira, abbreviateState } from '../utils/formatters';

interface SparklineProps {
  state: string;
  data: { year: number; total: number }[];
  isSelected: boolean;
  isHovered: boolean;
  selectedYear: number;
  minYear: number;
  maxYear: number;
  onClick: () => void;
  onHover: (hovered: boolean) => void;
}

function Sparkline({
  state,
  data,
  isSelected,
  isHovered,
  selectedYear,
  minYear,
  maxYear,
  onClick,
  onHover,
}: SparklineProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const pathRef = useRef<SVGPathElement | null>(null);
  const areaRef = useRef<SVGPathElement | null>(null);
  const dotRef = useRef<SVGCircleElement | null>(null);
  const gRef = useRef<SVGGElement | null>(null);
  const isInitializedRef = useRef(false);
  const prevYearRef = useRef<number>(selectedYear);
  
  const width = 120;
  const height = 40;
  const margin = { top: 4, right: 4, bottom: 4, left: 4 };
  const innerWidth = width - margin.left - margin.right;
  const innerHeight = height - margin.top - margin.bottom;
  
  // Create scales based on FULL data range (for consistent positioning)
  const { xScale, yScale } = useMemo(() => {
    const x = d3
      .scaleLinear()
      .domain([minYear, maxYear])
      .range([0, innerWidth]);
    
    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d) => d.total) ?? 0])
      .range([innerHeight, 0]);
    
    return { xScale: x, yScale: y };
  }, [data, minYear, maxYear, innerWidth, innerHeight]);
  
  // Line and area generators
  const lineGenerator = useMemo(() => {
    return d3
      .line<{ year: number; total: number }>()
      .x((d) => xScale(d.year))
      .y((d) => yScale(d.total))
      .curve(d3.curveMonotoneX);
  }, [xScale, yScale]);
  
  const areaGenerator = useMemo(() => {
    return d3
      .area<{ year: number; total: number }>()
      .x((d) => xScale(d.year))
      .y0(innerHeight)
      .y1((d) => yScale(d.total))
      .curve(d3.curveMonotoneX);
  }, [xScale, yScale, innerHeight]);
  
  // Calculate the path length up to the selected year
  const getPathLengthAtYear = useCallback((year: number): number => {
    if (!pathRef.current) return 0;
    
    const totalLength = pathRef.current.getTotalLength();
    const yearRange = maxYear - minYear;
    if (yearRange === 0) return totalLength;
    
    const progress = (year - minYear) / yearRange;
    return totalLength * progress;
  }, [minYear, maxYear]);
  
  // Initial setup - draw the full path but hide it with dashoffset
  useEffect(() => {
    if (!svgRef.current || data.length === 0) return;
    
    const svg = d3.select(svgRef.current);
    
    // Only initialize once
    if (!isInitializedRef.current) {
      svg.selectAll('*').remove();
      
      const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);
      gRef.current = g.node();
      
      // Create area path (full path, but we'll clip it)
      const areaPath = g.append('path')
        .datum(data)
        .attr('d', areaGenerator)
        .attr('fill', isSelected ? 'rgba(34, 211, 238, 0.15)' : 'rgba(100, 116, 139, 0.1)')
        .attr('stroke', 'none')
        .attr('class', 'sparkline-area');
      
      areaRef.current = areaPath.node();
      
      // Create line path (full path)
      const linePath = g.append('path')
        .datum(data)
        .attr('d', lineGenerator)
        .attr('fill', 'none')
        .attr('stroke', isSelected ? '#22d3ee' : '#64748b')
        .attr('stroke-width', isSelected ? 2 : 1.5)
        .attr('stroke-linecap', 'round')
        .attr('class', 'sparkline-line');
      
      pathRef.current = linePath.node();
      
      // Set up the dash pattern for partial reveal
      const totalLength = pathRef.current?.getTotalLength() || 0;
      const visibleLength = getPathLengthAtYear(selectedYear);
      
      linePath
        .attr('stroke-dasharray', totalLength)
        .attr('stroke-dashoffset', totalLength - visibleLength);
      
      // Create clipping path for the area
      const clipId = `clip-${state.replace(/\s/g, '-')}`;
      svg.append('defs')
        .append('clipPath')
        .attr('id', clipId)
        .append('rect')
        .attr('x', 0)
        .attr('y', 0)
        .attr('width', xScale(selectedYear))
        .attr('height', innerHeight + margin.top);
      
      areaPath.attr('clip-path', `url(#${clipId})`);
      
      // Create dot
      const currentYearData = data.find((d) => d.year === selectedYear);
      if (currentYearData) {
        const dot = g.append('circle')
          .attr('cx', xScale(currentYearData.year))
          .attr('cy', yScale(currentYearData.total))
          .attr('r', 0)
          .attr('fill', isSelected ? '#22d3ee' : '#94a3b8')
          .attr('stroke', '#0f172a')
          .attr('stroke-width', 1.5)
          .attr('class', 'sparkline-dot');
        
        dotRef.current = dot.node();
        
        // Bounce in the dot
        gsap.to(dotRef.current, {
          attr: { r: isSelected ? 4 : 3 },
          duration: 0.5,
          ease: 'elastic.out(1, 0.5)',
          delay: Math.random() * 0.2,
        });
      }
      
      // Animate initial line drawing
      gsap.to(pathRef.current, {
        strokeDashoffset: totalLength - visibleLength,
        duration: 0.8,
        ease: 'power2.out',
        delay: Math.random() * 0.3,
      });
      
      isInitializedRef.current = true;
      prevYearRef.current = selectedYear;
    }
  }, [data, lineGenerator, areaGenerator, xScale, yScale, margin.left, margin.top, innerHeight, state, getPathLengthAtYear, isSelected, selectedYear]);
  
  // Update colors when selection state changes
  useEffect(() => {
    if (!pathRef.current || !areaRef.current || !dotRef.current) return;
    
    gsap.to(pathRef.current, {
      stroke: isSelected ? '#22d3ee' : '#64748b',
      strokeWidth: isSelected ? 2 : 1.5,
      duration: 0.3,
    });
    
    gsap.to(areaRef.current, {
      fill: isSelected ? 'rgba(34, 211, 238, 0.15)' : 'rgba(100, 116, 139, 0.1)',
      duration: 0.3,
    });
    
    gsap.to(dotRef.current, {
      fill: isSelected ? '#22d3ee' : '#94a3b8',
      attr: { r: isSelected ? 4 : 3 },
      duration: 0.3,
    });
  }, [isSelected]);
  
  // Animate when year changes - smooth line extension/retraction
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (prevYearRef.current === selectedYear) return;
    if (!pathRef.current || !dotRef.current || !svgRef.current) return;
    
    const totalLength = pathRef.current.getTotalLength();
    const targetVisibleLength = getPathLengthAtYear(selectedYear);
    const targetDashOffset = totalLength - targetVisibleLength;
    
    // Animate the line to extend/retract to the new year
    gsap.to(pathRef.current, {
      strokeDashoffset: targetDashOffset,
      duration: 0.4,
      ease: 'power2.out',
    });
    
    // Update the clip rect for the area
    const clipRect = d3.select(svgRef.current).select(`#clip-${state.replace(/\s/g, '-')} rect`);
    gsap.to(clipRect.node(), {
      attr: { width: xScale(selectedYear) },
      duration: 0.4,
      ease: 'power2.out',
    });
    
    // Animate dot to new position
    const currentYearData = data.find((d) => d.year === selectedYear);
    if (currentYearData) {
      gsap.to(dotRef.current, {
        attr: {
          cx: xScale(currentYearData.year),
          cy: yScale(currentYearData.total),
        },
        duration: 0.4,
        ease: 'elastic.out(1, 0.6)',
      });
      
      // Quick pulse effect
      gsap.fromTo(dotRef.current,
        { attr: { r: isSelected ? 4 : 3 } },
        { 
          attr: { r: isSelected ? 6 : 5 },
          duration: 0.1,
          yoyo: true,
          repeat: 1,
          ease: 'power2.inOut',
        }
      );
    }
    
    prevYearRef.current = selectedYear;
  }, [selectedYear, data, xScale, yScale, getPathLengthAtYear, state, isSelected]);
  
  const currentValue = useMemo(() => {
    const d = data.find((d) => d.year === selectedYear);
    return d?.total ?? 0;
  }, [data, selectedYear]);
  
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => onHover(true)}
      onMouseLeave={() => onHover(false)}
      className={`
        p-2 rounded-lg transition-all duration-200 text-left
        ${isSelected 
          ? 'bg-accent-500/10 border border-accent-500/30' 
          : isHovered 
            ? 'bg-surface-800 border border-surface-600' 
            : 'bg-surface-900/50 border border-transparent hover:border-surface-700'
        }
      `}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`text-xs font-medium truncate ${isSelected ? 'text-accent-400' : 'text-surface-300'}`}>
          {abbreviateState(state)}
        </span>
        <span className="text-2xs text-surface-500 font-mono">
          {formatNaira(currentValue, true)}
        </span>
      </div>
      <svg ref={svgRef} width={width} height={height} className="w-full" />
    </button>
  );
}

export function SparklineGrid() {
  const { state, data, selectState, setHoveredState } = useApp();
  
  // Get year range
  const { minYear, maxYear } = useMemo(() => {
    if (!data || data.years.length === 0) return { minYear: 2007, maxYear: 2025 };
    return {
      minYear: data.years[0],
      maxYear: data.years[data.years.length - 1],
    };
  }, [data]);
  
  // Get sparkline data for all states
  const sparklineData = useMemo(() => {
    if (!data) return [];
    
    return data.states.map((stateName) => {
      const summary = data.stateSummaries.get(stateName);
      const yearData = data.years.map((year) => ({
        year,
        total: summary?.yearlyData.get(year)?.total ?? 0,
      }));
      return { state: stateName, data: yearData };
    });
  }, [data]);
  
  const handleClick = useCallback((stateName: string) => {
    selectState(stateName);
  }, [selectState]);
  
  const handleHover = useCallback((stateName: string, hovered: boolean) => {
    setHoveredState(hovered ? stateName : null);
  }, [setHoveredState]);
  
  if (!data) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="animate-pulse text-surface-400">Loading sparklines...</div>
      </div>
    );
  }
  
  return (
    <div className="card p-4">
      <h3 className="text-lg font-display font-semibold text-surface-100 mb-4">
        All States Overview
      </h3>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 max-h-[500px] overflow-y-auto no-scrollbar">
        {sparklineData.map(({ state: stateName, data: yearData }) => (
          <Sparkline
            key={stateName}
            state={stateName}
            data={yearData}
            isSelected={state.selectedStates.includes(stateName)}
            isHovered={state.hoveredState === stateName}
            selectedYear={state.selectedYear}
            minYear={minYear}
            maxYear={maxYear}
            onClick={() => handleClick(stateName)}
            onHover={(hovered) => handleHover(stateName, hovered)}
          />
        ))}
      </div>
      
      <p className="text-xs text-surface-500 mt-3 text-center">
        Click any state to view details • Line shows data up to {state.selectedYear}
      </p>
    </div>
  );
}
